import type { LiveCase, LiveCasePatch, LiveConflictField } from '@/domain/live-case';

import { api } from './client';

export type { LiveCase, LiveCasePatch, LiveConflictField };

let liveCasesCache: LiveCase[] | null = null;
let liveCasesInflight: Promise<LiveCase[]> | null = null;

/** 已有列表缓存时可用于首屏直出，避免切页再等一圈 */
export function peekLiveCasesCache(): LiveCase[] | null {
  return liveCasesCache;
}

export function initialLiveCasesLoadState(): { loading: boolean; rows: LiveCase[] } {
  const cached = peekLiveCasesCache();
  return {
    loading: !cached,
    rows: cached ? [...cached] : [],
  };
}

export function invalidateLiveCasesCache(): void {
  liveCasesCache = null;
}

function replaceLiveCasesCache(rows: LiveCase[]): LiveCase[] {
  liveCasesCache = rows;
  return rows;
}

function patchLiveCasesCacheRow(row: LiveCase): void {
  if (!liveCasesCache) return;
  const index = liveCasesCache.findIndex((item) => item.id === row.id);
  if (index >= 0) liveCasesCache[index] = row;
  else liveCasesCache.push(row);
}

export function fetchLiveCases(options?: { force?: boolean }): Promise<LiveCase[]> {
  if (!options?.force && liveCasesCache) {
    return Promise.resolve(liveCasesCache);
  }
  if (!options?.force && liveCasesInflight) {
    return liveCasesInflight;
  }
  liveCasesInflight = api
    .get<LiveCase[]>('/live-cases')
    .then((res) => replaceLiveCasesCache(res.data))
    .finally(() => {
      liveCasesInflight = null;
    });
  return liveCasesInflight;
}

export function createLiveCase(body: Record<string, unknown>): Promise<LiveCase> {
  return api.post<LiveCase>('/live-cases', body).then((res) => {
    patchLiveCasesCacheRow(res.data);
    return res.data;
  });
}

export function patchLiveCase(id: string, patch: LiveCasePatch): Promise<LiveCase> {
  return api.patch<LiveCase>(`/live-cases/${id}`, patch).then((res) => {
    patchLiveCasesCacheRow(res.data);
    return res.data;
  });
}

export function markLiveDefect(id: string): Promise<LiveCase> {
  return api.post<LiveCase>(`/live-cases/${id}/defect`).then((res) => {
    patchLiveCasesCacheRow(res.data);
    return res.data;
  });
}

export function undoLiveDefect(id: string): Promise<LiveCase> {
  return api.post<LiveCase>(`/live-cases/${id}/defect/undo`).then((res) => {
    patchLiveCasesCacheRow(res.data);
    return res.data;
  });
}

export function closeLiveCase(id: string): Promise<LiveCase> {
  return api.post<LiveCase>(`/live-cases/${id}/close`).then((res) => {
    patchLiveCasesCacheRow(res.data);
    return res.data;
  });
}

export function resolveLiveConflict(
  id: string,
  field: LiveConflictField,
  side: 'system' | 'excel',
): Promise<LiveCase> {
  return api.post<LiveCase>(`/live-cases/${id}/conflicts/${field}`, { side }).then((res) => {
    patchLiveCasesCacheRow(res.data);
    return res.data;
  });
}

export function bulkSaveLiveCases(
  rows: Array<{ id: string | null; patch: LiveCasePatch; create?: boolean; date?: string }>,
): Promise<LiveCase[]> {
  return api.put<LiveCase[]>('/live-cases', { rows }).then((res) => replaceLiveCasesCache(res.data));
}
